import React, { useState } from "react";
import { Box, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import TabPanel from "@mui/lab/TabPanel";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckIcon from "@mui/icons-material/Check";
import HomeIcon from "@mui/icons-material/Home";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { retrieveProcessOrders } from "./selector";
import { Product } from "../../../lib/types/product";
import { Messages, serverApi } from "../../../lib/config";
import { Order, OrderItem, OrderUpdateInput } from "../../../lib/types/order";
import { useGlobals } from "../../hooks/useGlobals";
import { OrderStatus } from "../../../lib/enums/order.enum";
import OrderService from "../../services/OrderService";
import { sweetErrorHandling } from "../../../lib/sweetAlert";
import { T } from "../../../lib/types/common";

/** REDUX SLICE & SELECTOR */
const processOrdersRetriever = createSelector(
  retrieveProcessOrders,
  (processOrders) => ({ processOrders })
);

function formatOrderDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

function getArrivingDate(order: Order): string {
  const d = typeof order.updatedAt === "string" ? new Date(order.updatedAt) : order.updatedAt;
  const arriving = new Date(d);
  arriving.setDate(arriving.getDate() + 5);
  return formatOrderDate(arriving);
}

function getFirstProduct(order: Order): Product | undefined {
  if (!order?.orderItems?.length || !order?.productData?.length) return undefined;
  const firstItem: OrderItem = order.orderItems[0];
  return order.productData.find((p: Product) => p._id === firstItem.productId);
}

function getProductForItem(order: Order, item: OrderItem): Product | undefined {
  return order.productData?.find((p: Product) => p._id === item.productId);
}

interface ProcessOrdersProps {
  setValue: (input: string) => void;
}

export default function ProcessOrders(props: ProcessOrdersProps) {
  const { setValue } = props;
  const { authMember, setOrderBuilder } = useGlobals();
  const { processOrders } = useSelector(processOrdersRetriever);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  const toggleViewDetails = (orderId: string) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  const finishOrderHandler = async (e: T) => {
    try {
      if (!authMember) throw new Error(Messages.error2);
      const orderid = (e.target as HTMLButtonElement).value;
      const input: OrderUpdateInput = {
        orderId: orderid,
        orderStatus: OrderStatus.FINISH,
      };

      const confirmation = window.confirm("Have you received your order?");
      if (confirmation) {
        const order = new OrderService();
        await order.updateOrder(input);
        setValue("3");
        setOrderBuilder(new Date());
      }
    } catch (err) {
      console.log(err);
      sweetErrorHandling(err).then();
    }
  };

  const handleTrackPackage = (orderId: string) => {
    // Placeholder: could open tracking URL or modal
    window.alert(`Track order ${orderId.slice(-6)} (tracking not integrated).`);
  };

  return (
    <TabPanel value={"2"}>
      <Stack className="process-orders-stack">
        {processOrders.map((order: Order) => {
          const firstProduct = getFirstProduct(order);
          const orderIdShort = `BK-${order._id.slice(-4)}`;
          const hasOtherProducts = (order.orderItems?.length ?? 0) > 1;
          const isExpanded = expandedOrderId === order._id;

          if (!firstProduct) return null;

          const imagePath = firstProduct.productImages?.length
            ? `${serverApi}/${firstProduct.productImages[0]}`
            : "/icons/noimage-list.svg";

          return (
            <Box key={order._id} className="process-order-card">
              {/* Header: truck icon + IN TRANSIT + Arriving by date | Order # | Track Package (no price) */}
              <Box className="process-order-tracking-header">
                <Box className="process-order-tracking-header-left">
                  <span className="process-order-tracking-icon-wrap">
                    <LocalShippingIcon className="process-order-truck-icon" />
                  </span>
                  <Box>
                    <span className="process-order-status-label">IN TRANSIT</span>
                    <p className="process-order-arriving">Arriving by {getArrivingDate(order)}</p>
                  </Box>
                </Box>
                <Box className="process-order-tracking-header-right">
                  <span className="process-order-id">Order #{orderIdShort}</span>
                  <span className="process-order-divider-v">|</span>
                  <button
                    type="button"
                    className="process-order-track-link"
                    onClick={() => handleTrackPackage(order._id)}
                  >
                    Track Package
                  </button>
                </Box>
              </Box>

              {/* Product card: image + title + author only (no price), then tracking line */}
              <Box className="paused-order-card-body">
                <img
                  src={imagePath}
                  alt={firstProduct.productName}
                  className="paused-order-product-img"
                />
                <Box className="paused-order-content">
                  <h3 className="paused-order-product-title">{firstProduct.productName}</h3>
                  <p className="paused-order-product-author">
                    {firstProduct.productAuthor || "No author"}
                  </p>
                  {/* Tracking line below author (smaller) */}
                  <Box className="process-order-steps process-order-steps--below-author process-order-steps--small">
                    <Box className="process-order-step process-order-step-done">
                      <span className="process-order-step-icon"><CheckIcon /></span>
                      <span className="process-order-step-label">Placed</span>
                    </Box>
                    <Box className="process-order-step-bar process-order-step-bar-done" />
                    <Box className="process-order-step process-order-step-done">
                      <span className="process-order-step-icon"><CheckIcon /></span>
                      <span className="process-order-step-label">Paid</span>
                    </Box>
                    <Box className="process-order-step-bar process-order-step-bar-done" />
                    <Box className="process-order-step process-order-step-current">
                      <span className="process-order-step-icon"><LocalShippingIcon /></span>
                      <span className="process-order-step-label">On Way</span>
                    </Box>
                    <Box className="process-order-step-bar" />
                    <Box className="process-order-step process-order-step-pending">
                      <span className="process-order-step-icon"><HomeIcon /></span>
                      <span className="process-order-step-label">Delivered</span>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Expanded: same rendering as Paused page (same classes) */}
              {isExpanded && hasOtherProducts && (
                <Box className="paused-order-details-list">
                  {order.orderItems.map((item: OrderItem) => {
                    const product = getProductForItem(order, item);
                    if (!product) return null;
                    const itemImg = product.productImages?.length
                      ? `${serverApi}/${product.productImages[0]}`
                      : "/icons/noimage-list.svg";
                    return (
                      <Box key={item._id} className="paused-order-detail-row">
                        <img src={itemImg} alt={product.productName} className="paused-order-detail-img" />
                        <Box className="paused-order-detail-info">
                          <span className="paused-order-detail-name">{product.productName}</span>
                          {product.productAuthor && (
                            <p className="paused-order-detail-author">{product.productAuthor}</p>
                          )}
                          <span className="paused-order-detail-qty">
                            {item.itemQuantity} × ${item.itemPrice.toFixed(2)} = $
                            {(item.itemQuantity * item.itemPrice).toFixed(2)}
                          </span>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Footer: Last scan + View details + Mark as Received */}
              <Box className="process-order-footer">
                <Box className="process-order-last-scan">
                  <LocationOnIcon className="process-order-last-scan-icon" />
                  <span>Last scan: In transit</span>
                </Box>
                <Box className="process-order-footer-actions">
                  {hasOtherProducts && (
                    <Button
                      variant="outlined"
                      className="process-order-view-details"
                      onClick={() => toggleViewDetails(order._id)}
                    >
                      {isExpanded ? "Hide details" : "View details"}
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    className="process-order-received-btn"
                    value={order._id}
                    onClick={finishOrderHandler}
                  >
                    Mark as Received
                  </Button>
                </Box>
              </Box>
            </Box>
          );
        })}

        {(!processOrders || processOrders.length === 0) && (
          <Box display="flex" flexDirection="row" justifyContent="center">
            <img
              src="/icons/noimage-list.svg"
              style={{ width: 300, height: 300 }}
              alt="No orders"
            />
          </Box>
        )}
      </Stack>
    </TabPanel>
  );
}
